import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import R from 'ramda';
import React, { MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';

import { OptionValue } from '../../types';

type Props = {
  className?: string;
  currency: Currency;
  hideVat?: boolean;
  onClick?: MouseEventHandler;
  optionChoices: OptionValue[];
  primaryLanguage: Language;
};

const List = styled(T.Body3)`
  color: ${cssThemedValue('textSecondary')};
`;

const ChoiceNameAndPrice = styled(T.Body3)<{ disabled: boolean }>`
  ${({ disabled }) =>
    disabled &&
    css`
      text-decoration: line-through;
    `}
`;

const Vat = styled(T.Small)``;

const OptionChoicesList = ({
  className,
  onClick,
  optionChoices,
  hideVat = false,
  primaryLanguage,
  currency,
}: Props) => {
  return (
    <List
      className={className}
      onClick={e => {
        e.preventDefault();
        if (onClick) {
          onClick(e);
        }
      }}
    >
      {R.intersperse<React.ReactNode>(
        ', ',
        optionChoices.map(choice => (
          <T.Body3 key={choice.id}>
            <ChoiceNameAndPrice disabled={choice.enabled.enabled === false} dir="auto" lang={primaryLanguage}>
              {localize(choice.name, primaryLanguage)} (
              <MonetaryAmount amount={choice.price} currency={currency} />)
            </ChoiceNameAndPrice>
            {!hideVat && choice.vat_percentage && (
              <Vat>
                {' '}
                <FormattedMessage
                  id="menu-item.vat-label"
                  values={{
                    percentage: (choice.vat_percentage / 100).toLocaleString(undefined, {
                      style: 'percent',
                      maximumFractionDigits: 2,
                    }),
                  }}
                />
              </Vat>
            )}
          </T.Body3>
        )),
      )}
    </List>
  );
};

const StyledPopover = styled(Popover)`
  text-overflow: ellipsis;
  overflow: hidden;
`;

const OptionChoicePopoverContent = styled.div`
  max-width: 640px;
  color: ${cssThemedValue('textPrimary')};
`;

export const OptionChoicesWithPopover = ({
  className,
  onClick,
  optionChoices,
  hideVat = false,
  primaryLanguage,
  currency,
}: Props) => {
  return (
    <StyledPopover
      className={className}
      content={
        <OptionChoicePopoverContent>
          <OptionChoicesList
            onClick={onClick}
            hideVat={hideVat}
            optionChoices={optionChoices}
            primaryLanguage={primaryLanguage}
            currency={currency}
          />
        </OptionChoicePopoverContent>
      }
      contentZIndex={100005}
      placement="bottom"
      delayOnMouseEnter={500}
      showOnClick={false}
    >
      <OptionChoicesList
        onClick={onClick}
        hideVat={hideVat}
        optionChoices={optionChoices}
        primaryLanguage={primaryLanguage}
        currency={currency}
      />
    </StyledPopover>
  );
};
